import axios from 'axios'
import { attachScriptActivity, publishScriptActivity } from '@/workspace/scriptActivity'
import { installAppVersionGuard } from '@/utils/appVersion'
import { ElMessage } from 'element-plus'
import { API_BASE_URL, TOKEN_KEY } from '@/config/constants'
import { installAuthGuard } from '@/utils/authSession'
import {
  createPermissionBlockedError,
  getBlockedPermissionRule,
  getEmptyPermissionResponse
} from '@/config/permissionGuards'

const request = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
})

let silentMode = false

installAppVersionGuard(request)
installAuthGuard(request, () => silentMode)

export const setSilentMode = (mode) => {
  silentMode = mode
}

export const withSilentMode = async (fn) => {
  const originalMode = silentMode
  silentMode = true
  try {
    return await fn()
  } finally {
    silentMode = originalMode
  }
}

request.interceptors.request.use(
  (config) => {
    if (config.expectedAuthToken !== undefined && localStorage.getItem(TOKEN_KEY) !== config.expectedAuthToken) {
      return Promise.reject(new Error('登录状态已变化，任务暂停'))
    }
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      // Do not inherit the JSON default: the browser must add the multipart boundary.
      config.headers['Content-Type'] = undefined
    }

    const blockedRule = getBlockedPermissionRule(config)
    if (blockedRule) {
      const emptyResponse = getEmptyPermissionResponse(blockedRule, config)
      if (emptyResponse) {
        config.adapter = () => Promise.resolve({
          data: emptyResponse,
          status: 200,
          statusText: 'OK',
          headers: {},
          config,
          request: null
        })
        return config
      }
      return Promise.reject(createPermissionBlockedError(blockedRule.permission))
    }

    const token = localStorage.getItem(TOKEN_KEY)
    if (token) {
      const tokenValue = token.startsWith('Bearer ') ? token.substring(7) : token
      config.headers.satoken = tokenValue
      config.__authToken = tokenValue
    }
    return attachScriptActivity(config)
  },
  (error) => {
    console.error('request error:', error)
    return Promise.reject(error)
  }
)

request.interceptors.response.use(
  async (response) => {
    let res = response.data
    if (response.config?.responseType === 'blob' && res instanceof Blob) {
      if (String(response.headers?.['content-type'] || '').includes('application/x-ndjson')) return res
      // JSON error envelopes can arrive with HTTP 200; never download them as a successful export.
      try { res = JSON.parse(await res.text()) } catch (_) { res = { message: '文件下载失败，请重试' } }
    }
    if (res.code === 200) {
      publishScriptActivity(response)
      return res
    }
    if (!silentMode && !response.config?.silent) ElMessage.error(res.message || 'Request failed')
    return Promise.reject(Object.assign(new Error(res.message || 'Request failed'), { code: res.code }))
  },
  (error) => {
    if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') return Promise.reject(error)
    if (error?.isVersionMismatch || error?.isAuthExpired) return Promise.reject(error)
    if (error?.isPermissionBlocked) {
      if (!silentMode && !error.config?.silent) ElMessage.error(error.message || 'Permission denied')
      return Promise.reject(error)
    }

    console.error('response error:', error)
    if (error.response) {
      const { status, data } = error.response
      error.code = data?.code ?? status
      error.status = status
      switch (status) {
        case 403:
          if (!silentMode && !error.config?.silent) ElMessage.error('Access denied')
          break
        case 404:
          if (!silentMode && !error.config?.silent) ElMessage.error('Request address not found')
          break
        case 500:
          if (!silentMode && !error.config?.silent) ElMessage.error('Internal server error')
          break
        default:
          if (!silentMode && !error.config?.silent) ElMessage.error(data?.message || `Request failed: ${status}`)
      }
    } else if (error.request) {
      if (!silentMode && !error.config?.silent) ElMessage.error('Network error')
    } else if (!silentMode && !error.config?.silent) {
      ElMessage.error(error.message || 'Request failed')
    }
    return Promise.reject(error)
  }
)

export default request
